/*
 * Brain2Canvas attribution capture for Shopify storefront forms.
 *
 * Purpose:
 * - capture Google Ads click IDs, UTMs, landing page, referrer, first seen time
 * - persist the payload in first-party cookie + localStorage
 * - populate Shopify Forms/custom forms with hidden attribution fields
 *
 * This script does not fire a lead conversion. `lead_submit` / GA4
 * `generate_lead` must only fire after a confirmed successful form submit.
 */
(function brain2canvasAttributionCapture() {
  'use strict';

  var STORAGE_KEY = 'b2c_attr_v1';
  var COOKIE_NAME = 'b2c_attr_v1';
  var VISITOR_COOKIE_NAME = 'b2c_visitor_id_v1';
  var SESSION_STORAGE_KEY = 'b2c_session_id_v1';
  var POSTED_EVENTS_KEY = 'b2c_jarvis_posted_events_v1';
  var THANK_YOU_TOKEN_KEY = 'b2c_ty_token_v1';
  var THANK_YOU_TOKEN_TTL_MS = 15 * 60 * 1000;
  var COOKIE_MAX_AGE = 90 * 24 * 60 * 60;
  var FIELD_KEYS = [
    'visitor_id',
    'session_id',
    'ga_client_id',
    'gclid',
    'gbraid',
    'wbraid',
    'gad_source',
    'gad_campaignid',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'utm_id',
    'utm_source_platform',
    'utm_creative_format',
    'utm_marketing_tactic',
    'landing_page',
    'referrer',
    'first_seen_at',
    'lead_id',
    'event_id',
    'thank_you_token',
    'thank_you_issued_at',
    'click_id',
    'gtm_attr_json'
  ];
  var URL_PARAM_KEYS = FIELD_KEYS.filter(function keepUrlKey(key) {
    return key !== 'visitor_id' && key !== 'session_id' && key !== 'ga_client_id'
      && key !== 'lead_id' && key !== 'event_id'
      && key !== 'thank_you_token' && key !== 'thank_you_issued_at'
      && key !== 'gtm_attr_json' && key !== 'landing_page' && key !== 'referrer'
      && key !== 'first_seen_at' && key !== 'click_id';
  });
  var lastLeadSubmit = null;
  var pendingPosts = {};
  var lastAttributionDataLayerValue = '';

  function nowIso() {
    return new Date().toISOString();
  }

  function uuid(prefix) {
    var random = '';
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      random = window.crypto.randomUUID();
    } else {
      random = String(Date.now()) + '-' + Math.random().toString(16).slice(2);
    }
    return prefix + '_' + random.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 64);
  }

  function readCookie(name) {
    var parts = document.cookie ? document.cookie.split('; ') : [];
    for (var i = 0; i < parts.length; i += 1) {
      var pair = parts[i].split('=');
      if (pair.shift() === name) return decodeURIComponent(pair.join('='));
    }
    return '';
  }

  function writeCookie(name, value) {
    document.cookie = name + '=' + encodeURIComponent(value) + '; path=/; max-age=' + COOKIE_MAX_AGE + '; samesite=lax';
  }

  function parseJson(value) {
    if (!value) return null;
    try {
      var parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (err) {
      return null;
    }
  }

  function compact(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function readSessionStorage(key) {
    try {
      return window.sessionStorage.getItem(key) || '';
    } catch (err) {
      return '';
    }
  }

  function writeSessionStorage(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (err) {
      // Session storage is a best-effort bridge; cookie/localStorage remain the attribution store.
    }
  }

  function gaClientId() {
    var ga = readCookie('_ga');
    var match = ga.match(/^GA\d+\.\d+\.(.+)$/);
    return match ? compact(match[1]) : '';
  }

  function stableVisitorIds() {
    var visitorId = compact(readCookie(VISITOR_COOKIE_NAME));
    if (!visitorId) {
      visitorId = uuid('visitor');
      writeCookie(VISITOR_COOKIE_NAME, visitorId);
    }

    var sessionId = compact(readSessionStorage(SESSION_STORAGE_KEY));
    if (!sessionId) {
      sessionId = uuid('session');
      writeSessionStorage(SESSION_STORAGE_KEY, sessionId);
    }

    return {
      visitor_id: visitorId,
      session_id: sessionId,
      ga_client_id: gaClientId()
    };
  }

  function currentScript() {
    return document.currentScript || Array.prototype.slice.call(document.scripts || []).filter(function findScript(script) {
      return /brain2canvas-attribution-capture\.js/.test(script.src || '');
    }).pop() || null;
  }

  function bridgeEndpoint() {
    var script = currentScript();
    var meta = document.querySelector('meta[name="b2c-jarvis-lead-endpoint"]');
    return compact(window.BRAIN2CANVAS_JARVIS_LEAD_ENDPOINT)
      || compact(meta && meta.getAttribute('content'))
      || compact(script && script.getAttribute('data-jarvis-lead-endpoint'));
  }

  function readPostedEvents() {
    try {
      return parseJson(window.localStorage.getItem(POSTED_EVENTS_KEY)) || {};
    } catch (err) {
      return {};
    }
  }

  function writePostedEvents(events) {
    try {
      window.localStorage.setItem(POSTED_EVENTS_KEY, JSON.stringify(events));
    } catch (err) {
      // Best effort only; endpoint idempotency is still keyed by event_id.
    }
  }

  function readState() {
    var local = '';
    try {
      local = window.localStorage.getItem(STORAGE_KEY) || window.localStorage.getItem('gtm_attr') || '';
    } catch (err) {
      local = '';
    }
    return parseJson(local) || parseJson(readCookie(COOKIE_NAME)) || parseJson(readCookie('gtm_attr')) || {};
  }

  function writeState(state) {
    var value = JSON.stringify(state);
    writeCookie(COOKIE_NAME, value);
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch (err) {
      // localStorage can be blocked; cookie remains the fallback.
    }
    if (value !== lastAttributionDataLayerValue) {
      lastAttributionDataLayerValue = value;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'b2c_attribution_ready',
        attribution: state
      });
    }
  }

  function flattenGtmAttr(target, gtmAttr) {
    if (!gtmAttr || typeof gtmAttr !== 'object') return;
    for (var i = 0; i < URL_PARAM_KEYS.length; i += 1) {
      var key = URL_PARAM_KEYS[i];
      if (!target[key] && compact(gtmAttr[key])) target[key] = compact(gtmAttr[key]);
    }
    if (gtmAttr.click_id && typeof gtmAttr.click_id === 'object') {
      var type = compact(gtmAttr.click_id.type);
      var value = compact(gtmAttr.click_id.value);
      if (type && value) {
        target.click_id = JSON.stringify({ type: type, value: value });
        if ((type === 'gclid' || type === 'gbraid' || type === 'wbraid') && !target[type]) target[type] = value;
      }
    }
    if (!target.referrer && gtmAttr.referrer && typeof gtmAttr.referrer === 'object') {
      target.referrer = compact(gtmAttr.referrer.full || gtmAttr.referrer.domain);
    }
  }

  function captureState() {
    var previous = readState();
    var next = {};
    Object.keys(previous).forEach(function copy(key) {
      if (FIELD_KEYS.indexOf(key) !== -1 && compact(previous[key])) next[key] = compact(previous[key]);
    });
    flattenGtmAttr(next, parseJson(readCookie('gtm_attr')));

    var params = new URLSearchParams(window.location.search || '');
    var hasFreshPaidSignal = false;
    URL_PARAM_KEYS.forEach(function applyParam(key) {
      var value = compact(params.get(key));
      if (value) {
        next[key] = value;
        hasFreshPaidSignal = true;
      }
    });

    ['gclid', 'gbraid', 'wbraid'].forEach(function markClickId(key) {
      if (next[key]) next.click_id = JSON.stringify({ type: key, value: next[key] });
    });

    var ids = stableVisitorIds();
    next.visitor_id = next.visitor_id || ids.visitor_id;
    next.session_id = ids.session_id || next.session_id;
    if (ids.ga_client_id) next.ga_client_id = ids.ga_client_id;

    if (!next.first_seen_at) next.first_seen_at = nowIso();
    if (hasFreshPaidSignal || !next.landing_page) next.landing_page = window.location.href;
    if (!next.referrer && document.referrer) next.referrer = document.referrer;

    writeState(next);
    return next;
  }

  function writeThankYouToken(payload, token) {
    var eventId = compact(payload && payload.event_id);
    var leadId = compact(payload && payload.lead_id);
    var value = {
      token: compact(token) || ('ty_' + eventId),
      event_id: eventId,
      lead_id: leadId,
      issued_at: Date.now(),
      expires_at: Date.now() + THANK_YOU_TOKEN_TTL_MS
    };
    writeSessionStorage(THANK_YOU_TOKEN_KEY, JSON.stringify(value));
  }

  function readValidThankYouToken() {
    var token = parseJson(readSessionStorage(THANK_YOU_TOKEN_KEY));
    if (!token || !token.event_id || !token.lead_id) return null;
    if (Number(token.expires_at || 0) < Date.now()) return null;
    return token;
  }

  function clearThankYouToken() {
    try {
      window.sessionStorage.removeItem(THANK_YOU_TOKEN_KEY);
    } catch (err) {
      // Best effort.
    }
  }

  function selectorForKey(key) {
    return [
      '[name="' + key + '"]',
      '[name="contact[' + key + ']"]',
      '[name="attributes[' + key + ']"]',
      '[name="contact[attributes][' + key + ']"]',
      '[data-b2c-attribution-key="' + key + '"]'
    ].join(',');
  }

  function ensureField(form, key) {
    var input = form.querySelector(selectorForKey(key));
    if (input) return input;
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.setAttribute('data-b2c-attribution-key', key);
    form.appendChild(input);
    return input;
  }

  function isLeadLikeForm(form) {
    if (!form || form.nodeType !== 1) return false;
    var action = compact(form.getAttribute('action')).toLowerCase();
    var identity = [
      form.getAttribute('id') || '',
      form.getAttribute('class') || '',
      form.getAttribute('name') || '',
      form.getAttribute('data-form-type') || '',
      form.getAttribute('data-type') || ''
    ].join(' ').toLowerCase();
    var text = (form.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();

    if (/\/(localization|search|cart|checkout)(\/|$)|\/cart\/add/.test(action)) return false;
    if (/localization|language|predictive-search|search-modal|cart-notification|newsletter|product-form|shopify-product-form|cow-form|cowlendar/.test(identity)) return false;
    if (/form contents shop-/.test(identity)) return false;
    if (/newsletter/.test(text) && !/workshop|lead|request|anfrage|team|event/.test(text)) return false;

    if (/contact|lead|request|anfrage|shopify-forms/.test(action + ' ' + identity)) return true;
    if (form.querySelector('textarea') && form.querySelector('[name*="ansprech"], [name*="unternehmen"], [name*="personen"], [name*="stadt"], [name*="workshop"], [name*="schreib"]')) return true;
    return false;
  }

  function fillForm(form, baseState) {
    if (!isLeadLikeForm(form)) return;
    var state = Object.assign({}, baseState);
    if (!form.dataset.b2cLeadId) form.dataset.b2cLeadId = uuid('lead');
    if (!form.dataset.b2cEventId) form.dataset.b2cEventId = uuid('event');
    state.lead_id = form.dataset.b2cLeadId;
    state.event_id = form.dataset.b2cEventId;
    state.gtm_attr_json = JSON.stringify(baseState);

    FIELD_KEYS.forEach(function fill(key) {
      var value = compact(state[key]);
      if (!value) return;
      ensureField(form, key).value = value;
    });
  }

  function fieldValue(form, patterns) {
    if (!form || !patterns || !patterns.length) return '';
    var fields = Array.prototype.slice.call(form.querySelectorAll('input, textarea, select'));
    for (var i = 0; i < fields.length; i += 1) {
      var field = fields[i];
      var identity = [
        field.name || '',
        field.id || '',
        field.getAttribute('aria-label') || '',
        field.getAttribute('placeholder') || '',
        field.getAttribute('data-testid') || ''
      ].join(' ').toLowerCase();
      var label = '';
      if (field.id) {
        var labelNode = form.querySelector('label[for="' + field.id.replace(/"/g, '\\"') + '"]');
        if (labelNode) label = compact(labelNode.textContent).toLowerCase();
      }
      var haystack = identity + ' ' + label;
      for (var p = 0; p < patterns.length; p += 1) {
        if (patterns[p].test(haystack)) return compact(field.value);
      }
    }
    return '';
  }

  function formPayload(form, reason) {
    var state = captureState();
    fillForm(form, state);
    var formIdentity = compact(form && (form.getAttribute('id') || form.getAttribute('name') || form.getAttribute('data-form-id') || form.getAttribute('data-testid')));
    return Object.assign({}, state, {
      event_id: compact(form && form.dataset && form.dataset.b2cEventId) || state.event_id || uuid('event'),
      lead_id: compact(form && form.dataset && form.dataset.b2cLeadId) || state.lead_id || uuid('lead'),
      form_id: formIdentity || 'shopify-forms-runtime',
      form_version: 'shopify-theme-bridge-v1',
      submitted_at: nowIso(),
      submit_page_url: window.location.href,
      bridge_reason: reason,
      email: fieldValue(form, [/email|e-mail|mail/]),
      name: fieldValue(form, [/name|ansprech/]),
      phone: fieldValue(form, [/phone|telefon|tel/]),
      company: fieldValue(form, [/company|firma|unternehmen/]),
      city: fieldValue(form, [/city|stadt|ort|location/]),
      team_size: fieldValue(form, [/person|teilnehmer|team.*size|group.*size|größe|groesse/]),
      preferred_date: fieldValue(form, [/date|datum|termin/]),
      workshop_interest: fieldValue(form, [/workshop|event|produkt|format/]),
      message: fieldValue(form, [/message|nachricht|schreib|note|kommentar/]),
      gtm_attr_json: JSON.stringify(state)
    });
  }

  function postLeadToJarvis(form, reason) {
    var endpoint = bridgeEndpoint();
    if (!form || !isLeadLikeForm(form)) return;
    if (!endpoint) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'b2c_jarvis_lead_bridge_missing_endpoint'
      });
      return;
    }
    var payload = formPayload(form, reason);
    var eventId = compact(payload.event_id);
    if (!eventId) return;
    var posted = readPostedEvents();
    if (posted[eventId] || pendingPosts[eventId]) return;
    pendingPosts[eventId] = true;

    window.fetch(endpoint, {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function onResponse(response) {
      if (!response || !response.ok) {
        throw new Error('Jarvis lead endpoint returned ' + (response ? response.status : 'no response'));
      }
      return response.json().catch(function emptyJson() { return {}; });
    }).then(function onBridgeOk(result) {
      posted[eventId] = nowIso();
      writePostedEvents(posted);
      writeThankYouToken(payload, result && result.thankYouToken);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'b2c_jarvis_lead_bridge_ok',
        attribution_lead_id: payload.lead_id,
        attribution_event_id: eventId
      });
    }).catch(function onError(err) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'b2c_jarvis_lead_bridge_error',
        attribution_event_id: eventId,
        error: compact(err && err.message).slice(0, 180)
      });
    }).finally(function clearPending() {
      delete pendingPosts[eventId];
    });
  }

  function fillAllForms() {
    var state = captureState();
    Array.prototype.forEach.call(collectForms(document), function eachForm(form) {
      fillForm(form, state);
    });
  }

  function collectForms(root) {
    var forms = [];
    if (!root || typeof root.querySelectorAll !== 'function') return forms;
    Array.prototype.forEach.call(root.querySelectorAll('form'), function addForm(form) {
      forms.push(form);
    });
    Array.prototype.forEach.call(root.querySelectorAll('*'), function scanShadow(element) {
      if (element.shadowRoot) {
        forms = forms.concat(collectForms(element.shadowRoot));
      }
    });
    return forms;
  }

  function bindSubmitAttempt() {
    document.addEventListener('submit', function onSubmit(event) {
      var form = event.target;
      if (!isLeadLikeForm(form)) return;
      var state = captureState();
      fillForm(form, state);
      lastLeadSubmit = { form: form, at: Date.now() };
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'b2c_form_submit_attempt',
        attribution_lead_id: form && form.dataset ? form.dataset.b2cLeadId : undefined,
        attribution_event_id: form && form.dataset ? form.dataset.b2cEventId : undefined
      });
    }, true);
  }

  function isShopifyFormsUrl(url) {
    var text = compact(url).toLowerCase();
    return /forms\.shopifyapps\.com\/api\/v2|shopify-forms|\/contact/.test(text);
  }

  function recentLeadForm() {
    if (!lastLeadSubmit || !lastLeadSubmit.form) return null;
    if (Date.now() - lastLeadSubmit.at > 30000) return null;
    return lastLeadSubmit.form;
  }

  function bindConfirmedSubmitBridge() {
    if (typeof window.fetch === 'function' && !window.fetch.__b2cJarvisLeadBridge) {
      var originalFetch = window.fetch;
      var wrappedFetch = function b2cFetchWrapper(input, init) {
        var url = compact(typeof input === 'string' ? input : input && input.url);
        var promise = originalFetch.apply(this, arguments);
        if (isShopifyFormsUrl(url)) {
          promise.then(function inspectResponse(response) {
            var form = recentLeadForm();
            if (form && response && response.ok) postLeadToJarvis(form, 'fetch_success');
          }).catch(function ignoreFetchError() {});
        }
        return promise;
      };
      wrappedFetch.__b2cJarvisLeadBridge = true;
      window.fetch = wrappedFetch;
    }

    if (window.XMLHttpRequest && !window.XMLHttpRequest.prototype.__b2cJarvisLeadBridge) {
      var originalOpen = window.XMLHttpRequest.prototype.open;
      var originalSend = window.XMLHttpRequest.prototype.send;
      window.XMLHttpRequest.prototype.open = function b2cXhrOpen(method, url) {
        this.__b2cUrl = compact(url);
        return originalOpen.apply(this, arguments);
      };
      window.XMLHttpRequest.prototype.send = function b2cXhrSend() {
        if (isShopifyFormsUrl(this.__b2cUrl)) {
          this.addEventListener('load', function onLoad() {
            var form = recentLeadForm();
            if (form && this.status >= 200 && this.status < 300) postLeadToJarvis(form, 'xhr_success');
          });
        }
        return originalSend.apply(this, arguments);
      };
      window.XMLHttpRequest.prototype.__b2cJarvisLeadBridge = true;
    }
  }

  function observeForms() {
    if (!window.MutationObserver) return;
    var observer = new MutationObserver(function onMutate() {
      fillAllForms();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  fillAllForms();
  bindSubmitAttempt();
  bindConfirmedSubmitBridge();
  observeForms();
  document.addEventListener('DOMContentLoaded', fillAllForms);
  window.setTimeout(fillAllForms, 1000);
    window.setTimeout(fillAllForms, 3000);

  function bindThankYouGate() {
    if (!/\/pages\/thank-you(\/|$)/.test(window.location.pathname || '')) return;
    var token = readValidThankYouToken();
    if (!token) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'b2c_thank_you_without_submit_token' });
      return;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'b2c_verified_thank_you',
      attribution_lead_id: token.lead_id,
      attribution_event_id: token.event_id,
      conversion_source: 'jarvis_verified_submit',
      token_age_seconds: Math.max(0, Math.round((Date.now() - Number(token.issued_at || Date.now())) / 1000))
    });
    clearThankYouToken();
  }

  bindThankYouGate();
}());
